import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { MarketingProgramResource, CreateMarketingProgramRequest } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface MarketingProgramFormProps {
  program?: MarketingProgramResource | null;
  onClose: () => void;
  onSubmit: () => void;
}

const MarketingProgramForm: React.FC<MarketingProgramFormProps> = ({
  program,
  onClose,
  onSubmit
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateMarketingProgramRequest>({
    name: '',
    description: '',
    type: 'no-coupon',
    programCode: '',
    couponPrefix: '',
    minimumMonth: 1,
    discountType: 'percent',
    discountValue: 0,
    isActive: true,
    usageLimit: undefined,
    usageLimitPerUser: undefined,
    startsAt: '',
    endsAt: '',
    expiredDay: undefined,
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Helper function to format date from API response to HTML date input format
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    // Convert "2025-09-01 00:00:00" to "2025-09-01"
    return dateString.split(' ')[0];
  };

  useEffect(() => {
    if (program) {
      setFormData({
        name: program.name,
        description: program.description || '',
        type: program.type,
        programCode: program.programCode || '',
        couponPrefix: program.couponPrefix || '',
        minimumMonth: program.minimumMonth,
        discountType: program.discountType,
        discountValue: program.discountValue,
        isActive: program.isActive,
        usageLimit: program.usageLimit,
        usageLimitPerUser: program.usageLimitPerUser,
        startsAt: formatDateForInput(program.startsAt),
        endsAt: formatDateForInput(program.endsAt),
        expiredDay: program.expiredDay,
      });
      if (program.image) {
        setImagePreview(program.image);
      }
    }
  }, [program]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: value ? Number(value) : undefined }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    
    if (formData.type === 'no-coupon' && !formData.programCode) {
      setError('Program code is required for no-coupon type');
      return;
    }
    
    if (formData.type === 'coupon' && !formData.couponPrefix) {
      setError('Coupon prefix is required for coupon type');
      return;
    }
    
    if (formData.type === 'no-coupon' && (!formData.startsAt || !formData.endsAt)) {
      setError('Start and end dates are required for no-coupon type');
      return;
    }
    
    if (formData.type === 'coupon' && !formData.expiredDay) {
      setError('Expired day is required for coupon type');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (program) {
        await api.updateMarketingProgram(program.id, formData);
      } else {
        await api.createMarketingProgram(formData);
      }
      onSubmit();
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {program ? 'Edit Marketing Program' : 'Create Marketing Program'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  disabled={loading}
                >
                  <option value="no-coupon">No Coupon</option>
                  <option value="coupon">Coupon</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Month *
                </label>
                <input
                  type="number"
                  name="minimumMonth"
                  value={formData.minimumMonth}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Program Specific Fields */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Program Configuration</h3>
              
              {formData.type === 'no-coupon' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Program Code *
                  </label>
                  <input
                    type="text"
                    name="programCode"
                    value={formData.programCode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    disabled={loading}
                  />
                </div>
              )}

              {formData.type === 'coupon' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coupon Prefix *
                  </label>
                  <input
                    type="text"
                    name="couponPrefix"
                    value={formData.couponPrefix}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    disabled={loading}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Type *
                </label>
                <select
                  name="discountType"
                  value={formData.discountType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  disabled={loading}
                >
                  <option value="percent">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Value *
                </label>
                <input
                  type="number"
                  name="discountValue"
                  value={formData.discountValue}
                  onChange={handleInputChange}
                  min="0"
                  step={formData.discountType === 'percent' ? '0.01' : '1'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  disabled={loading}
                />
                {formData.discountType === 'percent' && (
                  <p className="text-sm text-gray-500 mt-1">Enter percentage (e.g., 10 for 10%)</p>
                )}
                {formData.discountType === 'fixed' && (
                  <p className="text-sm text-gray-500 mt-1">Enter amount in IDR</p>
                )}
              </div>
            </div>
          </div>

          {/* Usage Limits */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Usage Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Usage Limit
                </label>
                <input
                  type="number"
                  name="usageLimit"
                  value={formData.usageLimit || ''}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">Leave empty for unlimited</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usage Limit Per User
                </label>
                <input
                  type="number"
                  name="usageLimitPerUser"
                  value={formData.usageLimitPerUser || ''}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">Leave empty for unlimited</p>
              </div>
            </div>
          </div>

          {/* Date Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Date Configuration</h3>
            
            {formData.type === 'no-coupon' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startsAt"
                    value={formData.startsAt}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endsAt"
                    value={formData.endsAt}
                    onChange={handleInputChange}
                    min={formData.startsAt}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {formData.type === 'coupon' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expired Day *
                </label>
                <input
                  type="number"
                  name="expiredDay"
                  value={formData.expiredDay || ''}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">Number of days until coupon expires</p>
              </div>
            )}
          </div>

          {/* Image Upload */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Image</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Program Image
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mx-auto h-32 w-32 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setFormData(prev => ({ ...prev, image: undefined }));
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        disabled={loading}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="image-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                        >
                          <span>Upload a file</span>
                          <input
                            id="image-upload"
                            name="image"
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={handleImageChange}
                            disabled={loading}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, JPEG up to 1MB</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              disabled={loading}
            />
            <label className="ml-2 block text-sm text-gray-900">
              Active
            </label>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Saving...</span>
                </div>
              ) : (
                program ? 'Update Program' : 'Create Program'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarketingProgramForm;
